import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, CircularProgress, IconButton, InputAdornment, Paper, TextField, Typography } from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import { useSelector } from "react-redux";
import * as Sentry from "@sentry/react";
import { Business, ErrorOutline, Lock, InfoOutlined, Visibility, VisibilityOff } from "@mui/icons-material";
import { ConfirmationDialog } from "@components/ConexionSat/ConfirmationDialog";
import {
  Button,
  Callout,
  ConsentRow,
  FieldWrapper,
  SkeletonTextoLegal,
  TypographyLegal,
  StepActions,
  PageHeader,
  SectionHeader,
  InfoBanner,
  EditRfcDialog,
} from "@components/ui_v2";
import { RootState, useAppDispatch } from "@store/store";
import { setCompanyData } from "@store/slices/registerSlice";
import { setModalErrorVisible } from "@store/slices/appSlice";
import {
  useCiecValidatorMutation,
  CiecValidatorRequest,
  BuroValidatorRequestPFAE,
  useBuroValidatorMutation,
} from "@api/sat_apis_new/satApsi";
import { useLazyGetLoanApplicationByIdQuery, usePostValidateNextStageMutation } from "@api/applications";
import { useLazyGetPersonsQuery } from "@api/personasApi";
import { usePatchCuentaMutation } from "@api/account";
import { ApiError } from "@interfaces/response";
import { EMPRESAS_BURO_PFAE, PATHS_TOFU_V2, STATUS_CATALOG, TAX_SYSTEM_TYPE } from "@helpers/constantes";
import { truncateText } from "@helpers/funciones";
import { RFC_PFAE, RFC_PM } from "@utils/expreciones";
import useInterval from "@hooks/useInterval";
import useStatusConfig from "@hooks/useStatusConfig";
import useInterfaceConfig from "@hooks/useInterfaceConfig";
import useTextosLegales from "@hooks/useTextosLegales";

const SECONDS_TO_FETCH = 10;
/** Mensaje con el que el API rechaza un RFC que ya pertenece a otra solicitud. */
const RFC_YA_REGISTRADO = "Error registro previo";
const CIEC_LENGTH = 8;
/**
 * Cuando la CIEC ya fue validada la contraseña no se vuelve a capturar, así que el campo se muestra
 * lleno con ceros (deshabilitado) en lugar de vacío. No es la clave real y nunca se envía al API:
 * `handleSubmit` no vuelve a llamar a `ciecValidation` si la solicitud ya está validada.
 */
const CIEC_YA_VALIDADA = "0".repeat(CIEC_LENGTH);

export const ConexionSAT = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const interfaceConfig = useInterfaceConfig();
  const { nextStatus, previousStatus } = useStatusConfig(STATUS_CATALOG.consulta_ciec.code);

  const companyData = useSelector((state: RootState) => state.register.company);
  const application = useSelector((state: RootState) => state.register.application);
  // Nombre de la marca (config de la base). Con marca propia: "tanto Lendia como <marca> no almacenan…"; en Lendia, solo
  // Lendia.
  const displayName = useSelector((state: RootState) => state.brand.display_name)?.trim();
  const quienNoAlmacena =
    displayName && displayName.toLowerCase() !== "lendia"
      ? `tanto Lendia como ${displayName} no almacenan`
      : "Lendia no almacena";
  const accountUser = useSelector((state: RootState) => state.register.account_user);
  const clientDetails = useSelector((state: RootState) => state.register.personal_data);
  const loanStatus = useSelector((state: RootState) => state.register.loan_status);
  const brand = useSelector((state: RootState) => state.brand);

  const isPM = application.tax_system_type === TAX_SYSTEM_TYPE.PM.code;
  const isPfae = application.tax_system_type === TAX_SYSTEM_TYPE.PFAE.code;
  const RFC_REGEX = isPM ? RFC_PM : RFC_PFAE;
  const RFC_MAX_LENGTH = isPM ? 12 : 13;
  const LABEL_RFC = isPM ? "RFC de la empresa" : "RFC de la PFAE";

  const [ciecValidation] = useCiecValidatorMutation();
  const [patchAccount] = usePatchCuentaMutation();
  const [getLoanApplication] = useLazyGetLoanApplicationByIdQuery();
  const [triggerPostNextStage] = usePostValidateNextStageMutation();
  const [buroValidator] = useBuroValidatorMutation();
  const [getPersonsByLoanId] = useLazyGetPersonsQuery();
  // Textos legales de la empresa; mientras cargan, skeleton en lugar del texto por defecto.
  const { textosLegales, cargandoTextos } = useTextosLegales("ConexionSAT");

  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [ciecStatus, setCiecStatus] = useState<"idle" | "pending" | "invalid">("idle");
  const [personId, setPersonId] = useState("");
  const [buroSended, setBuroSended] = useState(false);
  const [buroModalOpen, setBuroModalOpen] = useState(false);
  const [openEditRfc, setOpenEditRfc] = useState(false);
  const [rfcError, setRfcError] = useState("");

  // La CIEC ya validada deja el formulario en modo lectura: contraseña simulada y autorizaciones
  // marcadas, porque son datos que el usuario ya capturó y no se vuelven a pedir.
  const ciecYaValidada = companyData.is_correct_ciec;

  // Rama de las empresas en EMPRESAS_BURO_PFAE + PFAE: antes de avanzar de status, valida a la persona ante Buró de Crédito.
  const consultaBuroPfae = async (): Promise<boolean> => {
    try {
      if (EMPRESAS_BURO_PFAE.includes(interfaceConfig.name) && application.tax_system_type === TAX_SYSTEM_TYPE.PFAE.code) {
        if (buroSended) {
          return true;
        }

        const payload_buro: BuroValidatorRequestPFAE = {
          person_Type: "pf",
          data: {
            rfc: companyData.rfc,
            transaction_number: application.id as string,
            legal_name: truncateText(companyData.name || "", 26),
            first_name: truncateText(companyData.pfae_name || "", 26),
            second_name: "",
            father_last_name: truncateText(companyData.pfae_last_name || "", 26),
            mother_last_name: truncateText(companyData.pfae_last_name_2 || "", 26),
            reenter_NIP: true,
            authorization_label_response: true,
            loan_application_id: application.id as string,
            is_endorsement: false,
            person_id: personId,
          },
        };

        await buroValidator(payload_buro).unwrap();
      }

      return true;
    } catch (error) {
      const apiError = error as ApiError;
      Sentry.captureException(error, { extra: { component: "ConsultaBuro", values: {} } });
      dispatch(setModalErrorVisible({ open: true, error: apiError }));
      return false;
    }
  };

  const handleSaveRfc = async (rfc: string) => {
    // Mismo criterio que el modal de teléfono: si el valor no cambió, se cierra sin llamar al API.
    if (rfc === companyData.rfc) {
      setOpenEditRfc(false);
      return;
    }

    // Se limpia antes de cada intento para que el mismo error dos veces seguidas vuelva a mostrarse.
    setRfcError("");
    try {
      // El RFC vive en la cuenta (client_details) como `company_rfc`, igual que en RegistroDatosPersonales.
      await patchAccount({ accountId: clientDetails.id, body: { company_rfc: rfc } }).unwrap();
      dispatch(setCompanyData({ ...companyData, rfc }));
      setOpenEditRfc(false);
    } catch (error) {
      const apiError = error as ApiError;

      // El API responde 400 "Error registro previo" cuando el RFC ya está tomado por otra solicitud.
      // Es un error que el usuario puede corregir, así que se muestra en el campo y no en el modal
      // genérico, que además cerraría el flujo sin decirle qué hacer.
      if (apiError?.status === 400 && apiError?.data?.message === RFC_YA_REGISTRADO) {
        setRfcError("Este RFC ya está registrado en otra solicitud.");
        return;
      }

      dispatch(setModalErrorVisible({ open: true, error: apiError }));
      Sentry.captureException(apiError, { extra: { component: "ConexionSAT - Editar RFC" } });
    }
  };

  const getPersonByPfae = async () => {
    try {
      if (EMPRESAS_BURO_PFAE.includes(interfaceConfig.name) && application.tax_system_type === TAX_SYSTEM_TYPE.PFAE.code) {
        const responseGetPersons = await getPersonsByLoanId({ loanApplicationId: application.id }).unwrap();

        if (responseGetPersons.length > 0) {
          setPersonId(responseGetPersons[0].id as string);
          setBuroSended(responseGetPersons[0].is_credit_bureau_authorization_pf_sended as boolean);
        }
      }
    } catch (error) {
      console.error("Ha ocurrido un error al obtener las personas de la solicitud");
    }
  };

  const loanInterval = useInterval(async () => {
    try {
      if (!nextStatus) return;

      const loanApplication = await getLoanApplication(application.id).unwrap();
      if (loanApplication.is_correct_ciec === true) {
        loanInterval.setIsActiveInterval(false);

        const buroOk = await consultaBuroPfae();
        if (!buroOk) {
          setSubmitting(false);
          return;
        }

        await triggerPostNextStage({
          applicationId: application.id,
          statusCode: nextStatus.code,
          username: `TOFU - ${accountUser.email}`,
        }).unwrap();
        dispatch(setCompanyData({ ...companyData, is_correct_ciec: true }));
        setSubmitting(false);
        navigate(nextStatus?.url || "");
      } else if (loanApplication.is_correct_ciec === false) {
        loanInterval.setIsActiveInterval(false);
        setCiecStatus("invalid");
        setSubmitting(false);
      }
    } catch (error) {
      loanInterval.setIsActiveInterval(false);
      setSubmitting(false);
      console.error("Ha ocurrido un error al consultar el estatus de la solicitud", error);
    }
  }, SECONDS_TO_FETCH);

  // El efecto de abajo corre una sola vez, pero resuelve de forma asíncrona: sin esta referencia
  // haría el spread del `companyData` capturado en el primer render y borraría del store lo que
  // la sesión haya hidratado mientras tanto (RFC, razón social…).
  const companyDataRef = useRef(companyData);
  companyDataRef.current = companyData;

  useEffect(() => {
    const checkInitialStatus = async () => {
      try {
        const loanApplication = await getLoanApplication(application.id).unwrap();
        if (loanApplication.is_correct_ciec === true && !companyDataRef.current.is_correct_ciec) {
          dispatch(setCompanyData({ ...companyDataRef.current, is_correct_ciec: true }));
        }
        await getPersonByPfae();
      } catch (error) {
        console.error("Ha ocurrido un error al consultar el estatus de la solicitud", error);
      }
    };
    checkInitialStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formik = useFormik({
    initialValues: {
      ciec: "",
      satConsent: false,
      buroConsent: false,
    },
    validationSchema: yup.object().shape({
      ciec: yup.string().required("Campo requerido").length(CIEC_LENGTH, `Debe tener ${CIEC_LENGTH} caracteres`),
      satConsent: yup.boolean().oneOf([true], "Campo requerido"),
      buroConsent: yup.boolean().oneOf([true], "Campo requerido"),
    }),
    onSubmit: async (values) => {
      setCiecStatus("idle");

      // Con la CIEC ya validada no hay nada que revalidar: `values.ciec` son los ceros simulados,
      // así que solo se avanza de status.
      if (ciecYaValidada) {
        setSubmitting(true);
        try {
          if (loanStatus === STATUS_CATALOG.consulta_ciec.code && nextStatus) {
            await triggerPostNextStage({
              applicationId: application.id,
              statusCode: nextStatus.code,
              username: `TOFU - ${accountUser.email}`,
            }).unwrap();
          }
          // Siguiente pantalla según `status_config` (en Flux, uso de línea); la ruta fija queda de respaldo.
          navigate(nextStatus?.url || PATHS_TOFU_V2.usoDeCredito.path);
        } catch (error) {
          Sentry.captureException(error, { extra: { component: "ConexionSAT" } });
          dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
        } finally {
          setSubmitting(false);
        }
        return;
      }

      setSubmitting(true);
      setCiecStatus("pending");
      try {
        const payload: CiecValidatorRequest = {
          rfc: companyData.rfc,
          ciec: values.ciec,
          application_code: application.company_application_code,
          client_details_id: String(application.client_details_id),
          loan_application_conditions_id: String(application.application_condition_id),
          loan_application_id: String(application.id),
          requested_amount: Number(application.loan_amount),
          requested_term: Number(application.loan_term),
          company_id: brand?.id ?? "",
        };
        await ciecValidation(payload).unwrap();

        // El resultado llega por polling, así que `submitting` no se limpia aquí: lo hace el intervalo.
        loanInterval.setIsActiveInterval(true);
      } catch (error) {
        setSubmitting(false);
        setCiecStatus("idle");
        Sentry.captureException(error, { extra: { component: "ConexionSAT", values } });
        dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      }
    },
    validateOnMount: true,
  });

  /**
   * Precarga los datos que el usuario ya había capturado cuando la CIEC quedó validada: la
   * contraseña no se vuelve a pedir, así que el campo se llena con ceros y las autorizaciones
   * se marcan.
   *
   * Va en un efecto y no en `initialValues`: `is_correct_ciec` casi nunca está listo al montar
   * — llega cuando resuelve `checkInitialStatus` o la hidratación de la sesión — y para entonces
   * `initialValues` ya se evaluó. Es el mismo enfoque de la pantalla legacy `ConsultaCiec`.
   */
  useEffect(() => {
    if (!ciecYaValidada) return;
    formik.setValues({ ciec: CIEC_YA_VALIDADA, satConsent: true, buroConsent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ciecYaValidada]);

  const isFormDisabled = submitting || ciecStatus === "pending";
  // Los campos se bloquean también cuando la CIEC ya fue validada, pero sin entrar a `isFormDisabled`:
  // ese flag alimenta `nextDisabled` y dejaría el botón Continuar bloqueado para siempre.
  const areFieldsLocked = isFormDisabled || ciecYaValidada;
  const nextDisabled = !formik.isValid || isFormDisabled;

  if (ciecStatus === "pending" || ciecStatus === "invalid") {
    const isCiecInvalid = ciecStatus === "invalid";

    return (
      <Box>
        <PageHeader
          eyebrow="Empresa"
          title={isCiecInvalid ? "No pudimos validar tu CIEC" : "Estableciendo conexión con el SAT"}
          subtitle={
            isCiecInvalid
              ? "La contraseña SAT (CIEC) que ingresaste es incorrecta. Intenta nuevamente o restablece tu contraseña en el portal del SAT."
              : "Estamos validando tus credenciales. Esto puede tomar algunos minutos."
          }
        />

        <Paper
          variant="lendia_paper"
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            gap: 1.5,
            py: 8,
            px: 4,
          }}
        >
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: isCiecInvalid ? "error.light" : "primary.base",
            }}
          >
            {isCiecInvalid ? (
              <ErrorOutline sx={{ fontSize: 36, color: "error.main" }} />
            ) : (
              <CircularProgress size={36} thickness={4} />
            )}
          </Box>

          <Typography variant="h6" fontWeight={700}>
            {isCiecInvalid ? "Contraseña CIEC incorrecta" : "Procesando…"}
          </Typography>

          {isCiecInvalid && (
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 360 }}>
              Verifica que la escribiste correctamente y conserva mayúsculas, minúsculas y caracteres especiales.
            </Typography>
          )}

          {isCiecInvalid && (
            <Button tone="primary" sx={{ mt: 1 }} onClick={() => setCiecStatus("idle")}>
              Intentar de nuevo
            </Button>
          )}
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        // PFAE conecta su propio RFC (diseño `EmpresaCIEC` con `persona="PFAE"`); PM, el de la empresa.
        eyebrow={isPfae ? "Mi negocio · Conexión SAT" : "Empresa · Conexión SAT"}
        title={isPfae ? "Conecta tu RFC con el SAT" : "Conecta tu empresa con el SAT"}
        subtitle={`${
          isPfae
            ? "Con tu RFC y tu Contraseña SAT leemos tu información fiscal para evaluar la solicitud."
            : "Necesitamos consultar tu información fiscal para evaluar tu crédito."
        } Solo tomaremos información de lectura — ${quienNoAlmacena} tu CIEC.`}
      />

      <Paper variant="lendia_paper" sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 2 }}>
        <SectionHeader label="Credenciales SAT" first />

        <InfoBanner
          icon={<Business fontSize="small" />}
          label={LABEL_RFC}
          value={companyData.rfc || "—"}
          editLabel="Editar"
          onEdit={() => setOpenEditRfc(true)}
          editDisabled={companyData.is_correct_ciec || isFormDisabled}
        />

        <FieldWrapper
          label="Contraseña SAT (antes CIEC)"
          hint="Tu RFC como usuario + tu clave alfanumérica de 8 caracteres."
          full
          error={(formik.touched.ciec && formik.errors.ciec) || ""}
        >
          <TextField
            autoComplete="off"
            size="lendia_input"
            fullWidth
            type={showPwd ? "text" : "password"}
            name="ciec"
            value={formik.values.ciec}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            inputProps={{ maxLength: CIEC_LENGTH, "data-testid": "conexion-sat-ciec-input" }}
            disabled={areFieldsLocked}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setShowPwd((v) => !v)} edge="end" disabled={areFieldsLocked}>
                    {showPwd ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </FieldWrapper>

        {/* `.callout` informativo del diseño (no de advertencia): texto en azul marino e ícono en el color de marca. */}
        <Callout tone="info" icon={<Lock sx={{ fontSize: 16 }} />}>
          Tus credenciales viajan cifradas con TLS 1.3 y se usan únicamente durante este análisis.
        </Callout>
      </Paper>

      <Paper variant="lendia_paper" sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        <SectionHeader label="Autorizaciones requeridas" first />

        {/* Sin superficie propia: la card de la sección ya la aporta. */}
        <ConsentRow
          bare
          checked={formik.values.satConsent}
          onChange={(v) => formik.setFieldValue("satConsent", v)}
          // Mientras cargan los textos no se puede marcar: se autorizaría sin haber leído el texto.
          disabled={areFieldsLocked || cargandoTextos}
          // Texto legal con la razón social de la marca (`companyNames`), como `SAT_CHECK` del diseño.
          label={
            cargandoTextos ? (
              <SkeletonTextoLegal lineas={2} />
            ) : (
              <TypographyLegal type="autorizacionSat" texto={textosLegales.ciec?.check_sat?.texto} />
            )
          }
        />
        <ConsentRow
          bare
          checked={formik.values.buroConsent}
          onChange={(v) => {
            formik.setFieldValue("buroConsent", v);
            if (v) setBuroModalOpen(true);
          }}
          disabled={areFieldsLocked || cargandoTextos}
          label={
            cargandoTextos ? (
              <SkeletonTextoLegal lineas={2} />
            ) : (
              <TypographyLegal type="autorizacionBuro" texto={textosLegales.ciec?.check_buro?.texto} />
            )
          }
        />

        {/* `.callout` informativo del diseño, con el texto en 12.5px (`BURO_AVISO`). */}
        <Callout tone="info" icon={<InfoOutlined sx={{ fontSize: 16 }} />}>
          <Box component="span" sx={{ fontSize: 12.5, lineHeight: 1.55 }}>
            {cargandoTextos ? (
              <SkeletonTextoLegal lineas={3} />
            ) : (
              <TypographyLegal type="avisoEvaluacion" texto={textosLegales.ciec?.aviso?.texto} />
            )}
          </Box>
        </Callout>
      </Paper>

      <StepActions
        // Regreso secuencial según `status_config`; la ruta fija queda de respaldo.
        onBack={() => navigate(previousStatus?.url || PATHS_TOFU_V2.decisionIngreso.path)}
        onNext={formik.handleSubmit}
        nextDisabled={nextDisabled}
      />

      <ConfirmationDialog
        open={buroModalOpen}
        title={textosLegales.ciec?.modal_buro?.titulo?.trim() || "Autorización de consulta Buró"}
        onClose={() => setBuroModalOpen(false)}
      >
        {/* Mismo aspecto que el texto legacy (`CustomTextCiecBuro`): centrado y en el azul marino de la marca. */}
        <Typography component="div" variant="body2" color="primary.dark" textAlign="center" sx={{ mb: 3 }}>
          {cargandoTextos ? (
            <SkeletonTextoLegal lineas={8} />
          ) : (
            <TypographyLegal
              type="modalBuro"
              parrafos={textosLegales.ciec?.modal_buro?.parrafos}
              secciones={textosLegales.ciec?.modal_buro?.secciones}
            />
          )}
        </Typography>
      </ConfirmationDialog>

      <EditRfcDialog
        open={openEditRfc}
        initialValue={companyData.rfc || ""}
        onSubmit={handleSaveRfc}
        onClose={() => {
          setRfcError("");
          setOpenEditRfc(false);
        }}
        disabled={companyData.is_correct_ciec}
        maxLength={RFC_MAX_LENGTH}
        pattern={RFC_REGEX}
        error={rfcError}
        title={`Actualizar ${LABEL_RFC}`}
        fieldLabel={LABEL_RFC}
        hint="Con este RFC consultaremos tu información ante el SAT."
      />
    </Box>
  );
};
